import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseApiPollingOptions {
  /** Poll interval; `null` / `undefined` / `0` = one-shot (manual refetch only). */
  intervalMs?: number | null
  /** When false, skip fetch + polling. Default true. */
  enabled?: boolean
  /** Fetch immediately on mount. Default true. */
  immediate?: boolean
  /** Extra values that should re-trigger fetch (e.g. filter query params). */
  deps?: readonly unknown[]
}

export interface UseApiPollingResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  lastUpdated: Date | null
  refetch: (opts?: { refresh?: boolean }) => Promise<void>
}

/**
 * MEV-564 / MEV-565 — shared fetch/poll with AbortController.
 * Aborts the in-flight request on unmount, option change, and each new tick
 * so stale responses cannot setState after unmount or overwrite newer data.
 */
export function useApiPolling<T>(
  fetcher: (ctx: { signal: AbortSignal; refresh?: boolean }) => Promise<T>,
  options: UseApiPollingOptions = {},
): UseApiPollingResult<T> {
  const { intervalMs = null, enabled = true, immediate = true, deps = [] } = options
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState<boolean>(Boolean(immediate && enabled))
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  // Serialize deps so callers can pass inline arrays without breaking the effect identity contract.
  const depsKey = JSON.stringify(deps)

  const refetch = useCallback(async (opts?: { refresh?: boolean }) => {
    if (!enabled) return
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    try {
      const next = await fetcherRef.current({
        signal: controller.signal,
        refresh: opts?.refresh,
      })
      if (controller.signal.aborted || !mountedRef.current) return
      setData(next)
      setError(null)
      setLastUpdated(new Date())
    } catch (err) {
      if (controller.signal.aborted || !mountedRef.current) return
      const message = err instanceof Error ? err.message : 'Request failed'
      // Ignore intentional aborts surfaced as DOMException
      if (err instanceof DOMException && err.name === 'AbortError') return
      if (typeof message === 'string' && /abort/i.test(message)) return
      setError(message)
    } finally {
      if (!controller.signal.aborted && mountedRef.current) {
        setLoading(false)
      }
    }
  }, [enabled])

  useEffect(() => {
    mountedRef.current = true
    if (!enabled) {
      setLoading(false)
      return () => {
        mountedRef.current = false
        abortRef.current?.abort()
      }
    }

    if (immediate) {
      void refetch()
    }

    let intervalId: ReturnType<typeof setInterval> | undefined
    if (intervalMs && intervalMs > 0) {
      intervalId = setInterval(() => {
        void refetch()
      }, intervalMs)
    }

    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
      if (intervalId) clearInterval(intervalId)
    }
  }, [enabled, immediate, intervalMs, refetch, depsKey])

  return { data, loading, error, lastUpdated, refetch }
}

/** Bearer token from the canonical localStorage key. */
export function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('mev_shield_token')
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

/**
 * fetch wrapper that always attaches mev_shield_token (MEV-567).
 * Throws on !res.ok with a short message.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = {
    ...getAuthHeaders(),
    ...(init.headers || {}),
  }
  const res = await fetch(input, { ...init, headers })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
  return res
}
